import { MatterDto, MatterPayerForm, MatterPayerWriteDto } from 'types/matters';
import { t } from 'utils/getTranslations';

/** The share every case's payers add up to, in percent. */
export const PAYER_SHARE_TOTAL = 100;

/** What the share input accepts while it is typed: up to three whole digits and two decimals, dot or comma. */
export const SHARE_RATE_PATTERN = /^\d{0,3}([.,]\d{0,2})?$/;

export const shareRateValue = (text: string | null | undefined): number => {
    const value = Number((text ?? '').replace(/,/g, '.'));
    return Number.isFinite(value) ? value : 0;
};

const roundShare = (value: number): number => Math.round(value * 100) / 100;

export const isShareOutOfRange = (text: string | null | undefined): boolean => {
    const value = shareRateValue(text);
    return value < 0 || value > PAYER_SHARE_TOTAL;
};

export const formatShareRate = (value: number): string => roundShare(value).toFixed(2);

/** The row of the case's payer, the first and main one, before a payer is picked. */
export const emptyPrimaryPayerRow = (): MatterPayerForm => ({
    party: null,
    shareRate: String(PAYER_SHARE_TOTAL),
});

/** The form's payer rows for a case, main payer first: its own list, or its single payer at the full share. */
export const payerRowsFromDto = (matter: Pick<MatterDto, 'payers' | 'payerParty'>): MatterPayerForm[] => {
    if (matter.payers?.length) {
        return matter.payers.map((payer) => ({
            party: payer.party,
            shareRate: String(payer.shareRate),
        }));
    }
    return [{ ...emptyPrimaryPayerRow(), party: matter.payerParty ?? null }];
};

/**
 * The rows a new case takes over from a source case: its list, or only its main payer for someone who may not
 * manage several payers, as creating the case with the whole list would be refused.
 */
export const copiedPayerRows = (
    matter: Pick<MatterDto, 'payers' | 'payerParty'>,
    canManagePayers: boolean
): MatterPayerForm[] =>
    canManagePayers ? payerRowsFromDto(matter) : [{ ...emptyPrimaryPayerRow(), party: matter.payerParty ?? null }];

export const payerShareTotal = (rows: MatterPayerForm[]): number =>
    roundShare(rows.reduce((sum, row) => sum + shareRateValue(row.shareRate), 0));

/** The case's payer: the first row, the main payer. */
export const primaryPayerParty = (rows: MatterPayerForm[]): MatterPayerForm['party'] => rows[0]?.party ?? null;

/**
 * The payer list a save sends, in its order: the rows with a party, a single payer always at the full share. Empty
 * when no payer is picked, which clears the stored list.
 */
export const payerRowsToDto = (rows: MatterPayerForm[]): MatterPayerWriteDto[] => {
    const picked = rows.filter(
        (row): row is MatterPayerForm & { party: NonNullable<MatterPayerForm['party']> } => !!row.party
    );
    return picked.map((row) => ({
        partyId: row.party.id,
        shareRate: picked.length === 1 ? PAYER_SHARE_TOTAL : roundShare(shareRateValue(row.shareRate)),
    }));
};

/** A row to add: it takes what the others leave of the total, so one added to a complete list starts at 0. */
export const newPayerRow = (rows: MatterPayerForm[]): MatterPayerForm => ({
    party: null,
    shareRate: formatShareRate(Math.max(0, PAYER_SHARE_TOTAL - payerShareTotal(rows))),
});

/** The rows with the chosen one first, which makes it the main payer; the others keep their order. */
export const withPayerOnTop = (rows: MatterPayerForm[], index: number): MatterPayerForm[] => [
    rows[index],
    ...rows.filter((_, rowIndex) => rowIndex !== index),
];

/** The parties the other rows already hold, which a row's picker leaves out: a party pays only once. */
export const partyIdsOfOtherRows = (rows: MatterPayerForm[], index: number): number[] =>
    rows.flatMap((row, rowIndex) => (rowIndex !== index && row.party ? [row.party.id] : []));

/** The rows without one; removing the first makes the next one the main payer. */
export const withoutPayer = (rows: MatterPayerForm[], removedIndex: number): MatterPayerForm[] =>
    rows.filter((_, index) => index !== removedIndex);

/** The react-hook-form rule of the payer list; a single payer is always valid, whatever its share says. */
export const validatePayerRows = (rows: MatterPayerForm[]): true | string => {
    if (rows.length <= 1) {
        return true;
    }
    if (rows.some((row) => !row.party)) {
        return t('matters.payers.party-required');
    }
    const partyIds = rows.map((row) => row.party?.id);
    if (new Set(partyIds).size !== partyIds.length) {
        return t('matters.payers.party-once');
    }
    if (rows.some((row) => isShareOutOfRange(row.shareRate))) {
        return t('matters.payers.share-range');
    }
    const total = payerShareTotal(rows);
    if (total !== PAYER_SHARE_TOTAL) {
        return t('matters.payers.shares-total-error', { total: formatShareRate(total) });
    }
    return true;
};
